import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { pdfjs } from 'react-pdf';

import {
  cleanString,
  isSignatureToken,
  normalizeSignatureToken,
} from '../constants';

const getItemBox = (item, viewport) => {
  const [, , , height, x, baselineY] = item.transform || [];
  const itemHeight = Math.abs(height || item.height || 0);
  const left = x || 0;
  const top = viewport.height - (baselineY || 0) - itemHeight;

  return {
    left,
    top,
    right: left + (item.width || 0),
    bottom: top + itemHeight,
  };
};

export default function useLabelsExtraction({
  defaultSignatures,
  defaultFunctions,
}) {
  const [labels, setLabels] = useState(defaultSignatures?.labels);
  const [labelsMatch, setLabelsMatch] = useState(
    defaultSignatures?.labelsMatch,
  );
  const [labelsRevision, setLabelsRevision] = useState(0);
  const [signatureAnchors, setSignatureAnchors] = useState([]);
  const [fieldFunction, setFieldFunction] = useState(defaultFunctions ?? {});
  const [newLabel, setNewLabel] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [openFunctions, setOpenFunctions] = useState({});
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && labels && Object.keys(labels).length > 0) {
      const initialState = {};
      Object.keys(labels).forEach((key) => {
        const cleanedKey = cleanString(key);
        initialState[cleanedKey] = (fieldFunction[key]?.length ?? 0) > 0;
      });
      setOpenFunctions(initialState);
      initialized.current = true;
    }
  }, [labels, fieldFunction]);

  //Lee el PDF y obtiene las etiquetas(Campos)
  const getTextFromPDF = async (pdfFile, { preserveExisting = false } = {}) => {
    const pdf = await pdfjs.getDocument(pdfFile).promise;
    let extractedStructures = [];
    const extractedSignatureAnchors = [];

    const regex = /{{\s*(.*?)\s*}}/g;

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item) => item.str).join(' ');
      const sanitizedText = pageText
        .replace(/\{\s+/g, '{')
        .replace(/\s+\}/g, '}');

      const matches = sanitizedText.match(regex)?.map((match) => {
        return match.replace(/\s+/g, '');
      });

      if (matches) {
        extractedStructures = [...extractedStructures, ...matches];
      }

      const viewport = page.getViewport({
        scale: 1,
      });
      const items = textContent.items || [];
      const itemRanges = [];
      const joinedItemsText = items.reduce((value, item) => {
        const start = value.length;
        const nextValue = `${value}${item.str || ''}`;
        itemRanges.push({
          item,
          start,
          end: nextValue.length,
        });
        return nextValue;
      }, '');
      let occurrence = 0;

      const signatureRegex = /\{\{\s*sign:[a-zA-Z0-9_.-]+\s*\}\}/gi;
      let signatureMatch = signatureRegex.exec(joinedItemsText);
      while (signatureMatch) {
        const normalizedToken = normalizeSignatureToken(signatureMatch[0]);

        if (isSignatureToken(normalizedToken)) {
          const matchStart = signatureMatch.index;
          const matchEnd = matchStart + signatureMatch[0].length;
          const matchedItems = itemRanges
            .filter(({ start, end }) => end > matchStart && start < matchEnd)
            .map(({ item }) => getItemBox(item, viewport));

          const box = matchedItems.reduce((result, itemBox) => ({
            left: Math.min(result.left, itemBox.left),
            top: Math.min(result.top, itemBox.top),
            right: Math.max(result.right, itemBox.right),
            bottom: Math.max(result.bottom, itemBox.bottom),
          }), {
            left: viewport.width,
            top: viewport.height,
            right: 0,
            bottom: 0,
          });

          occurrence += 1;
          extractedSignatureAnchors.push({
            key: `${pageNum}-${normalizedToken}-${occurrence}`,
            token: normalizedToken,
            occurrence,
            page: pageNum,
            pageWidth: viewport.width,
            pageHeight: viewport.height,
            relative_position_x: (box.left / viewport.width) * 100,
            relative_position_y: (box.top / viewport.height) * 100,
          });
        }
        signatureMatch = signatureRegex.exec(joinedItemsText);
      }
    }

    const uniqueLabels = [...new Set(extractedStructures)].filter(
      (label) => !isSignatureToken(label),
    );
    const labelObject = uniqueLabels.reduce((acc, label) => {
      acc[label] = preserveExisting ? labelsMatch?.[label] || '' : '';
      return acc;
    }, {});

    setLabels(labelObject);
    setLabelsMatch(labelObject);
    setLabelsRevision((revision) => revision + 1);
    if (!preserveExisting) {
      setFieldFunction({});
      setOpenFunctions({});
    } else {
      const nextFunctions = Object.keys(labelObject).reduce((result, key) => ({
        ...result,
        ...(fieldFunction[key]
          ? {
            [key]: fieldFunction[key],
          }
          : {}),
      }), {});
      setFieldFunction(nextFunctions);
    }
    setSignatureAnchors(extractedSignatureAnchors);
  };

  const handleAddLabel = () => {
    if (newLabel.trim() !== '' && !labels[newLabel]) {
      setLabels((prev) => ({
        ...prev,
        [newLabel]: '',
      }));
      setLabelsMatch((prev) => ({
        ...prev,
        [newLabel]: '',
      }));
      setNewLabel('');
      setIsAdding(false);
    }
  };

  return {
    labels,
    labelsMatch,
    labelsRevision,
    signatureAnchors,
    fieldFunction,
    openFunctions,
    newLabel,
    isAdding,
    setLabels,
    setLabelsMatch,
    setLabelsRevision,
    setFieldFunction,
    setOpenFunctions,
    setNewLabel,
    setIsAdding,
    getTextFromPDF,
    handleAddLabel,
  };
}
